import type { StructuralElementType } from './element-type';
import type { FoundationFactors, FoundationPieceMeasure } from './foundation-factors';
import type { ElementGeometry } from './geometry';
import { ownFactorsByKey } from './own-factor';
import type { SteelRate } from './steel';
import type { StructuralElement, StructuralElementKind } from './structural-element';

/**
 * O fator e a taxa de aço peça a peça no pilar e na viga (Fase 42, etapas 1 e 2 — G1, G2, G5, G6;
 * RN-563, RN-564, RN-567 a RN-569; ADR-188).
 *
 * É a conta da fundação (`foundation-factors.ts`) com outra peça: a medida do serviço se reparte
 * pilar a pilar pela medida de cada um, e cada parte leva o fator que vale nele — o próprio do
 * card, o do tipo ou o índice da obra.
 *
 * ```text
 * concreto dos pilares = Σ medida do serviço × parte do pilar × (próprio ?? tipo ?? índice)
 * aço dos pilares      = Σ volume do pilar × Σ bitolas da taxa que vale nele (própria ?? tipo ?? disciplina)
 * ```
 *
 * A medida de cada peça é a desta conta (`ElementGeometry.ownMeasures`): a viga que corre na
 * divisa é meia de cada apartamento, como no total do serviço. As peças saem da geometria já
 * feita, e não de novo: o pilar só mede com as paredes e os trechos dele.
 *
 * A taxa de aço entra na peça (`steelRates`), e o resto é o da fundação: a taxa equivalente do
 * serviço (`foundationSteelRates`), o aço de cada peça (`foundationPieceSteel`) e o item sem bitola
 * repartido pelo aço de cada uma (`foundationShares`, RN-556).
 */

const viva = (peca: { readonly deletedAt?: string }): boolean => peca.deletedAt === undefined;

/** A lista que vale: a vazia é "nenhuma", e quem chama passa para a de baixo. */
const cheia = (taxas: readonly SteelRate[] | undefined): readonly SteelRate[] | undefined =>
  taxas === undefined || taxas.length === 0 ? undefined : taxas;

/**
 * O fator que vale num pilar ou numa viga, item a item: a composição do tipo — do mesmo elemento,
 * vivo —, com o próprio do card por cima. O item que nenhum dos dois tem fica de fora, e a conta
 * usa o índice da obra.
 */
export function elementFactors(
  element: Pick<StructuralElement, 'kind' | 'elementTypeId' | 'factors'>,
  types: ReadonlyMap<string, Pick<StructuralElementType, 'kind' | 'rates' | 'deletedAt'>>,
): Readonly<Record<string, number>> {
  const tipo = element.elementTypeId === undefined ? undefined : types.get(element.elementTypeId);
  const doTipo =
    tipo === undefined || !viva(tipo) || tipo.kind !== element.kind
      ? {}
      : ownFactorsByKey(tipo.rates);
  return { ...doTipo, ...ownFactorsByKey(element.factors) };
}

/**
 * A taxa de aço que vale num pilar ou numa viga (Fase 42, etapa 2 — G6; RN-568): a própria; sem
 * ela, a do tipo — do mesmo elemento, vivo. Ausente: a da disciplina. A lista vale inteira, como
 * no bloco (RN-544) — misturar bitolas de duas taxas daria um aço que ninguém calculou.
 */
export function elementSteelRates(
  element: Pick<StructuralElement, 'kind' | 'elementTypeId' | 'steelRates'>,
  types: ReadonlyMap<string, Pick<StructuralElementType, 'kind' | 'steelRates' | 'deletedAt'>>,
): readonly SteelRate[] | undefined {
  const tipo = element.elementTypeId === undefined ? undefined : types.get(element.elementTypeId);
  return (
    cheia(element.steelRates) ??
    (tipo === undefined || !viva(tipo) || tipo.kind !== element.kind
      ? undefined
      : cheia(tipo.steelRates))
  );
}

/**
 * A medida, o fator e a taxa de aço de cada pilar e viga vivos desta conta — sempre, mesmo sem
 * fator nem taxa. A peça que a geometria não mediu (a órfã, cujas paredes saíram do desenho) fica
 * de fora, como no total.
 */
export function structurePieces(input: {
  readonly elements?: readonly StructuralElement[];
  readonly elementTypes?: readonly StructuralElementType[];
  /** A geometria desta conta (`computeGeometry(...).elements`). */
  readonly geometry: readonly ElementGeometry[];
}): FoundationFactors<StructuralElementKind> {
  const tipos = new Map((input.elementTypes ?? []).map((tipo) => [String(tipo.id), tipo]));
  const medidas = new Map(input.geometry.map((medida) => [String(medida.elementId), medida]));
  const factors: Record<string, Readonly<Record<string, number>>> = {};
  const pieces: FoundationPieceMeasure<StructuralElementKind>[] = [];
  for (const element of input.elements ?? []) {
    if (!viva(element)) continue;
    const medida = medidas.get(String(element.id));
    if (medida === undefined) continue;
    const taxa = elementSteelRates(element, tipos);
    pieces.push({
      pieceId: element.id,
      element: element.kind,
      measures: medida.ownMeasures,
      ...(taxa === undefined ? {} : { steelRates: taxa }),
    });
    const fatores = elementFactors(element, tipos);
    if (Object.keys(fatores).length > 0) factors[element.id] = fatores;
  }
  return { pieces, factors };
}

/**
 * Só quando faz diferença: `undefined` sem nenhum pilar ou viga com tipo de composição, fator
 * próprio ou taxa de aço — aí a conta é a de sempre, o índice da obra × a medida somada e o volume
 * × a taxa da disciplina, e não precisa ver peça nenhuma. A pergunta barata vem antes da
 * geometria: quem chama só a faz quando há o que repartir (`hasElementFactors`).
 */
export function structureFactorsOf(
  input: Parameters<typeof structurePieces>[0],
): FoundationFactors<StructuralElementKind> | undefined {
  const base = structurePieces(input);
  const nada =
    Object.keys(base.factors).length === 0 &&
    !base.pieces.some((peca) => peca.steelRates !== undefined);
  return nada ? undefined : base;
}

/**
 * Algum pilar ou viga vivo com fator próprio, com taxa de aço própria, ou com um tipo que tem
 * composição ou taxa? A pergunta que vem antes da geometria: sem nenhum, a conta da tipologia não
 * precisa medir peça a peça.
 */
export function hasElementFactors(
  elements: readonly Pick<
    StructuralElement,
    'kind' | 'elementTypeId' | 'factors' | 'steelRates' | 'deletedAt'
  >[],
  types: readonly Pick<
    StructuralElementType,
    'id' | 'kind' | 'rates' | 'steelRates' | 'deletedAt'
  >[],
): boolean {
  const tipos = new Map(types.map((tipo) => [String(tipo.id), tipo]));
  return elements.some(
    (element) =>
      viva(element) &&
      (Object.keys(elementFactors(element, tipos)).length > 0 ||
        elementSteelRates(element, tipos) !== undefined),
  );
}
